import {
  CommonHelperFunctions,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IDesign,
  IDisplayItem,
  IScreenDesign,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";

export interface ISetCollapsePanelSymbolsGroupState {}

export interface ISetCollapsePanelSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild) => void;
}

class SetCollapsePanelSymbolsGroup extends PureSdComponent<
  ISetCollapsePanelSymbolsGroupProps & IWithSDProps & IWithContextProp,
  ISetCollapsePanelSymbolsGroupState
> {
  getParameterFixedValue = (parameterName: string): string | undefined => {
    const parameter: IActionParameter | undefined = this.props.parameters.find(
      (element: IActionParameter) => element.name === parameterName
    );
    if (parameter?.symbol?.fixedValue === undefined || parameter?.symbol?.fixedValue === null) {
      return undefined;
    }
    return parameter.symbol.fixedValue.toString();
  };

  buildUpdatedParameter = (
    parameterName: string,
    fixedValue: string | number | undefined
  ): IActionParameter | undefined => {
    const parameter: IActionParameter | undefined = this.props.parameters.find(
      (element: IActionParameter) => element.name === parameterName
    );
    if (parameter === undefined) {
      return undefined;
    }
    const nextParameter: IActionParameter = _.cloneDeep(parameter);
    if (!nextParameter.symbol) {
      nextParameter.symbol = {} as ISymbol;
    }
    nextParameter.symbol.fixedValue = fixedValue;
    nextParameter.symbol.category = ESymbolCategory.fixed;
    return nextParameter;
  };

  handleCollapseSelect = (collapseDiId: string): void => {
    const parametersToUpdate: IActionParameter[] = [];
    const componentNameParameter: IActionParameter | undefined = this.buildUpdatedParameter(
      "componentName",
      collapseDiId
    );
    if (componentNameParameter === undefined) {
      return;
    }
    parametersToUpdate.push(componentNameParameter);

    // A panel belongs to a single Collapse — clear a stale panel selection on target change.
    const selectedPanelKey: string | undefined = this.getParameterFixedValue("panelKey");
    const nextPanels: IDisplayItem[] = this.getPanelsOfCollapse(collapseDiId);
    const isPanelStillValid: boolean = nextPanels.some(
      (panel: IDisplayItem) => panel.id.toString() === selectedPanelKey
    );
    if (selectedPanelKey !== undefined && !isPanelStillValid) {
      const panelKeyParameter: IActionParameter | undefined = this.buildUpdatedParameter("panelKey", "");
      if (panelKeyParameter !== undefined) {
        parametersToUpdate.push(panelKeyParameter);
      }
    }

    this.props.updateValues({ actionParameters: parametersToUpdate } as IActionPanelStatesCanBeUpdatedFromChild);
  };

  handlePanelSelect = (panelDiId: string): void => {
    const panelKeyParameter: IActionParameter | undefined = this.buildUpdatedParameter("panelKey", panelDiId);
    if (panelKeyParameter === undefined) {
      return;
    }
    this.props.updateValues({ actionParameters: [panelKeyParameter] } as IActionPanelStatesCanBeUpdatedFromChild);
  };

  getAllDisplayItems = (): IDisplayItem[] => {
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    const result: IDisplayItem[] = [];
    this.fillAllDisplayItemsRecursive(sd, result);
    return result;
  };

  getAllDisplayItemsOfDi = (di: IDisplayItem, result: IDisplayItem[]): void => {
    if (di.children)
      for (let i = 0; i < di.children.length; i++) {
        if (!di.children[i].friendlyName) {
          di.children[i].friendlyName = di.children[i].id.toString();
        }
        result.push(di.children[i]);
        this.getAllDisplayItemsOfDi(di.children[i], result);
      }
  };

  fillAllDisplayItemsRecursive = (sd: IDesign, result: IDisplayItem[]): void => {
    if (sd.displayItems)
      for (let i = 0; i < sd.displayItems.length; i++) {
        if (!sd.displayItems[i].friendlyName) {
          sd.displayItems[i].friendlyName = sd.displayItems[i].id.toString();
        }
        result.push(sd.displayItems[i]);
        this.getAllDisplayItemsOfDi(sd.displayItems[i], result);
      }
  };

  getCollapses = (): IDisplayItem[] => {
    const collapses: IDisplayItem[] = this.getAllDisplayItems().filter(
      (di: IDisplayItem) => di.item?.name === "Collapse"
    );
    collapses.sort((a: IDisplayItem, b: IDisplayItem) =>
      CommonHelperFunctions.compareObjectWithField(a, b, "friendlyName")
    );
    return collapses;
  };

  getPanelsOfCollapse = (collapseDiId: string | undefined): IDisplayItem[] => {
    if (collapseDiId === undefined) {
      return [];
    }
    const collapse: IDisplayItem | undefined = this.getAllDisplayItems().find(
      (di: IDisplayItem) => di.id.toString() === collapseDiId
    );
    if (!collapse?.children) {
      return [];
    }
    return collapse.children.filter((child: IDisplayItem) => child.item?.name === "CollapsePanel");
  };

  renderSelect = (
    label: string,
    value: string | undefined,
    options: KuikaSelectboxOption[],
    onChange: (nextValue: string) => void
  ): JSX.Element => {
    return (
      <>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          {label}
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            searchable
            size="sm"
            style={{ width: "100%" }}
            value={value ?? null}
            onChange={onChange}
            className="kuika_add_actions_inputs_size"
            options={options}
          />
        </div>
      </>
    );
  };

  render(): JSX.Element {
    const collapses: IDisplayItem[] = this.getCollapses();
    const selectedCollapseId: string | undefined = this.getParameterFixedValue("componentName");
    const panels: IDisplayItem[] = this.getPanelsOfCollapse(selectedCollapseId);
    const selectedPanelKeyValue: string | undefined = this.getParameterFixedValue("panelKey");
    const selectedPanelKey: string | undefined = selectedPanelKeyValue === "" ? undefined : selectedPanelKeyValue;

    const collapseOptions: KuikaSelectboxOption[] = collapses.map((di: IDisplayItem) => ({
      key: di.id.toString(),
      label: di.friendlyName || di.id.toString(),
      value: di.id.toString()
    }));
    const panelOptions: KuikaSelectboxOption[] = panels.map((di: IDisplayItem) => ({
      key: di.id.toString(),
      label: di.friendlyName || di.id.toString(),
      value: di.id.toString()
    }));

    return (
      <div style={{ width: "100%" }}>
        {this.renderSelect("Collapse to Change", selectedCollapseId, collapseOptions, this.handleCollapseSelect)}
        {this.renderSelect("Panel", selectedPanelKey, panelOptions, this.handlePanelSelect)}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const setCollapsePanelSymbolsGroup = withContext<ISetCollapsePanelSymbolsGroupProps>(
  withSD<ISetCollapsePanelSymbolsGroupProps>(SetCollapsePanelSymbolsGroup)
);
export { setCollapsePanelSymbolsGroup as SetCollapsePanelSymbolsGroup };
